import { CloudFunction } from '@tomis-tech/types';

import { invokeFunction } from '../../cloud-functions';

/** Payload returned from Twilio validator (v2)
 *
 * https://www.twilio.com/docs/lookup/v2-api#making-a-request
 */
type TwilioNumberValidator = {
  callingCountryCode?: string;
  countryCode?: string;
  phoneNumber?: string;
  nationalFormat?: string;
  valid: boolean;
  validationErrors: string[];
  callerName?: string;
  simSwap?: string;
  callForwarding?: boolean;
  liveActivity?: boolean;
  lineTypeIntelligence?: string;
  url: string;
};

/** Phone number must be at least 10 digits long.
 * We are using a free twilio service to check
 * if phone number is valid. Learn more here:
 *
 * https://www.twilio.com/blog/validate-phone-number-input
 * https://www.twilio.com/docs/lookup/api
 *
 * @param {string} phone Must be international format
 */
export async function validatePhoneNumber({
  phone,
  countryCode,
}: {
  /** National format phone number */
  phone: string;
  /** Country code for national phone number lookups */
  countryCode: string;
}): Promise<{
  valid: boolean;
  errorMessage?: string;
  data?: TwilioNumberValidator;
}> {
  if (phone.length < 6) {
    return {
      valid: false,
      errorMessage: 'Phone number must be at least 6 digits long.',
    };
  }

  const [body, response] = await invokeFunction<TwilioNumberValidator>({
    name: CloudFunction.Api,
    body: { number: phone, countryCode },
    fullUrl: 'api/validatePhoneNumber',
  });

  if (response.status >= 300 || !body) {
    // unexpected error occurred
    return {
      valid: false,
      errorMessage:
        'Unable to validate your phone number, you may not receive a text from us.',
    };
  } else {
    return {
      // check if phone number is valid
      valid: body.valid,
      data: body,
    };
  }
}
